/**
 * Analytics Service - Frontend tracking for KPI metrics
 *
 * This service handles all user interaction tracking including:
 * - Property views
 * - Property clicks (contact, WhatsApp, call, email)
 * - Favorites
 * - Shares
 * - Search events
 */

import { resolveApiBaseUrl } from '../utils/apiClient';
import { getAuthToken } from '../utils/authToken';

const API_BASE = resolveApiBaseUrl();

// Session ID for anonymous user tracking
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('safaqat24_session_id');
  if (!sessionId) {
    sessionId = `sess_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    sessionStorage.setItem('safaqat24_session_id', sessionId);
  }
  return sessionId;
};
// NOTE: the auth token lives inside the session object at `safaqat24.auth.session`
// (read via utils/authToken.getAuthToken). The old `localStorage.getItem('auth_token')`
// here always returned null, so every engagement event was sent ANONYMOUSLY even
// for signed-in users — breaking per-user attribution. Fixed by using the shared helper.

// Track an event
async function trackEvent(
  type: string,
  entityType: string,
  entityId?: string,
  data?: Record<string, unknown>
): Promise<void> {
  try {
    const sessionId = getSessionId();
    const token = getAuthToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Session-ID': sessionId,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Send tracking event asynchronously - don't block user interaction.
    // `keepalive` lets the POST finish even when the click immediately navigates
    // the tab away (e.g. tel:/wa.me/mailto links) — without it the in-flight
    // request is cancelled and the WHATSAPP/CALL/EMAIL event is never recorded.
    fetch(`${API_BASE}/events/track`, {
      method: 'POST',
      headers,
      keepalive: true,
      body: JSON.stringify({
        type,
        entityType,
        entityId,
        data,
        pageUrl: window.location.href,
        referrer: document.referrer,
      }),
    }).catch((error) => {
      // Silently fail - don't disrupt user experience
      console.debug('Analytics tracking failed:', error);
    });
  } catch (error) {
    console.debug('Analytics tracking error:', error);
  }
}

/**
 * Track a property view
 */
export function trackPropertyView(propertyId: string, propertyData?: {
  title?: string;
  price?: number;
  city?: string;
  propertyType?: string;
}): void {
  trackEvent('VIEW', 'property', propertyId, propertyData);
}

/**
 * Track a property click (contact button, details, etc.)
 */
export function trackPropertyClick(propertyId: string, action: string, data?: Record<string, unknown>): void {
  trackEvent('CLICK', 'property', propertyId, { action, ...data });
}

/**
 * Track a contact action (WhatsApp, call, email)
 */
export function trackContact(
  entityType: 'property' | 'agent' | 'company',
  entityId: string,
  method: 'whatsapp' | 'call' | 'email'
): void {
  const eventType = method === 'whatsapp' ? 'WHATSAPP'
    : method === 'call' ? 'CALL'
    : method === 'email' ? 'EMAIL'
    : 'CONTACT';

  trackEvent(eventType, entityType, entityId, { method });
}

/**
 * Track a favorite action
 */
export function trackFavorite(propertyId: string, action: 'add' | 'remove'): void {
  trackEvent('FAVORITE', 'property', propertyId, { action });
}

/**
 * Track a share action
 */
export function trackShare(
  entityType: 'property' | 'agent' | 'company',
  entityId: string,
  platform: string
): void {
  trackEvent('SHARE', entityType, entityId, { platform });
}

/**
 * Track a search event
 */
export function trackSearch(
  query: string,
  filters: Record<string, unknown>,
  resultsCount: number
): void {
  trackEvent('SEARCH', 'search', undefined, {
    query,
    filters,
    resultsCount,
  });
}

/**
 * Track page navigation
 */
export function trackPageView(pageName: string, pageData?: Record<string, unknown>): void {
  trackEvent('VIEW', 'page', pageName, pageData);
}

/**
 * Track agent profile view
 */
export function trackAgentView(agentId: string, agentData?: {
  name?: string;
  company?: string;
}): void {
  trackEvent('VIEW', 'agent', agentId, agentData);
}

/**
 * Track company profile view
 */
export function trackCompanyView(companyId: string, companyData?: {
  name?: string;
}): void {
  trackEvent('VIEW', 'company', companyId, companyData);
}

/**
 * Track a neighborhood / area view. Powers the admin Neighborhood Intelligence
 * dashboard — real per-area viewership accumulates from the public site.
 */
export function trackAreaView(areaId: string, areaData?: {
  nameEn?: string;
  nameAr?: string;
  cityId?: string;
}): void {
  trackEvent('VIEW', 'area', areaId, areaData);
}

/**
 * Track engagement metrics (scroll depth, time on page)
 */
export function trackEngagement(
  entityType: string,
  entityId: string,
  metrics: {
    scrollDepth?: number;
    timeOnPage?: number;
  }
): void {
  trackEvent('VIEW', entityType, entityId, {
    engagement: true,
    ...metrics,
  });
}

// Hook for tracking scroll depth
let scrollDepthTracked = { 25: false, 50: false, 75: false, 100: false };
let currentEntityId: string | null = null;
let currentEntityType: string | null = null;
let pageEntryTime: number | null = null;

export function initScrollTracking(entityType: string, entityId: string): () => void {
  // Reset scroll tracking
  scrollDepthTracked = { 25: false, 50: false, 75: false, 100: false };
  currentEntityId = entityId;
  currentEntityType = entityType;
  pageEntryTime = Date.now();

  const handleScroll = () => {
    const scrollPercent = Math.round(
      (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
    );

    const thresholds = [25, 50, 75, 100] as const;
    for (const threshold of thresholds) {
      if (scrollPercent >= threshold && !scrollDepthTracked[threshold]) {
        scrollDepthTracked[threshold] = true;
        trackEngagement(currentEntityType!, currentEntityId!, { scrollDepth: threshold });
      }
    }
  };

  window.addEventListener('scroll', handleScroll);

  // Cleanup function
  return () => {
    window.removeEventListener('scroll', handleScroll);

    // Track time on page when leaving
    if (pageEntryTime && currentEntityId && currentEntityType) {
      const timeOnPage = Math.round((Date.now() - pageEntryTime) / 1000);
      if (timeOnPage > 5) { // Only track if user spent more than 5 seconds
        trackEngagement(currentEntityType, currentEntityId, { timeOnPage });
      }
    }
  };
}

// Export default object for convenience
const analytics = {
  trackPropertyView,
  trackPropertyClick,
  trackContact,
  trackFavorite,
  trackShare,
  trackSearch,
  trackPageView,
  trackAgentView,
  trackCompanyView,
  trackAreaView,
  trackEngagement,
  initScrollTracking,
};

export default analytics;
